import { useFormik } from 'formik';
import { ISelect, ParamEnum } from '../../../../../shared/types/shared';
import { useEffect, useState } from 'react';
import { asyncGetParams } from '../../../../../services/param.service';
import { PqrResultWeb } from '../../../../../shared/types/modules/pqr';
import { initialValues, validationSchema } from './formData';
import { asyncFindPublicPqr } from '../../../../../services/pqr.service';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { PQRWEB_SURVEY_RESPONSE } from '../../../../../routes/paths';

export const usePqrFilters = (survey?: boolean) => {
    const [types, setTypes] = useState<ISelect[]>([]);
    const [results, setResults] = useState<PqrResultWeb | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const isCloseStatus =
        results?.info?.status?.code === `${ParamEnum.PQR_STATUS}_CER` &&
        !survey;

    const isSolvedStatus =
        results?.info?.status?.code === `${ParamEnum.PQR_STATUS}_SOL` &&
        !survey;

    const isMessageInfo =
        (!survey &&
            (results?.info?.status?.code === `${ParamEnum.PQR_STATUS}_PRO` ||
                results?.info?.status?.code ===
                    `${ParamEnum.PQR_STATUS}_AMP`)) ||
        results?.info?.status?.code === `${ParamEnum.PQR_STATUS}_ANU`;

    const isResponseSurvey = Boolean(results?.survey);
    const handleFormSubmit = async () => {
        setHasSearched(true);
        setIsLoading(true);
        const filters = {
            consecutiveFormat: formik.values.consecutiveFormat || '',
            document: formik.values.document || '',
            typeDocument: formik.values.typeDocument as string,
        };
        try {
            const result = await asyncFindPublicPqr(filters);
            setResults((result as PqrResultWeb) || null);
            setIsLoading(false);

            if (
                result &&
                (result as PqrResultWeb)?.info?.status?.code ===
                    `${ParamEnum.PQR_STATUS}_CER` &&
                !(result as PqrResultWeb)?.survey &&
                survey
            ) {
                navigate(
                    `${PQRWEB_SURVEY_RESPONSE}/${(result as PqrResultWeb)?.id}`
                );
            }
        } catch (error) {
            setResults(null);
            setIsLoading(false);
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: handleFormSubmit,
    });

    useEffect(() => {
        asyncGetParams(ParamEnum.DOCUMENT_TYPE).then((params) =>
            setTypes(params)
        );
    }, []);

    const handleFormClear = async (e?: React.MouseEvent<HTMLButtonElement>) => {
        formik.resetForm();
        setResults(null);
        setHasSearched(false);
        setIsLoading(false);
        if (e?.type === 'click') {
            navigate(location.pathname, { replace: true });
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'N/A';

        const date = dayjs(dateString);
        const day = date.format('DD');
        const month = date.format('MM');
        const year = date.format('YYYY');

        return `${day}/${month}/${year}`;
    };

    const getSearchStatusMessage = (
        statusCode: string,
        statusManagement: any,
        area: string,
        responseMedium: string
    ): string => {
        switch (statusCode) {
            case `${ParamEnum.PQR_STATUS}_ABI`:
            case `${ParamEnum.PQR_STATUS}_REC`:
                return `Nos permitimos informar que la PQR consultada se encuentra en revisión por la Gerencia de Servicio para su escalamiento a nivel interno y será atendido dentro de los términos de Ley.`;
            case `${ParamEnum.PQR_STATUS}_TRA`:
            case `${ParamEnum.PQR_STATUS}_SEG`:
            case `${ParamEnum.PQR_STATUS}_REP`:
                return `Nos permitimos informar que la PQRS consultada se encuentra en revisión y análisis por parte de nuestra Área ${area}, quien estará emitiendo respuesta dentro de los términos de Ley.`;
            case `${ParamEnum.PQR_STATUS}_PRO`:
                return `Nos permitimos informar que para la PQR consultada se solicitó prórroga el ${formatDate(
                    statusManagement.extensionDate
                )} y la respuesta se emitirá el ${formatDate(
                    statusManagement.furagDate
                )}.`;
            case `${ParamEnum.PQR_STATUS}_AMP`:
                return `Nos permitimos informar que para la PQR consultada se solicitó ampliación de información al cliente el ${formatDate(
                    statusManagement.suspensionDate
                )}, para poder dar trámite, en caso de no recibir respuesta por parte del cliente el ${formatDate(
                    statusManagement.furagDate
                )}, se procederá al cierre como desistida.`;
            case `${ParamEnum.PQR_STATUS}_CER`:
                if (isResponseSurvey) {
                    return `Nos permitimos informar que la PQR consultada fue concluida el ${formatDate(
                        statusManagement.responseDate
                    )} y ya se diligenció una encuesta.`;
                }
                return `Nos permitimos informar que la PQR consultada fue concluida el ${formatDate(
                    statusManagement.responseDate
                )} y la respuesta fue enviada por ${responseMedium}.`;
            case `${ParamEnum.PQR_STATUS}_SOL`:
                return `Nos permitimos informar que la PQR consultada fue concluida el ${formatDate(
                    statusManagement.responseDate
                )} y la respuesta fue enviada por ${responseMedium}.
                `;
            case `${ParamEnum.PQR_STATUS}_ANU`:
                return `Nos permitimos informar que la PQR consultada se encuentra en estado anulada por encontrarse duplicada o repetida en el sistema.`;
            default:
                return 'No se encontraron resultados.';
        }
    };

    const getSurveyStatusMessage = (statusCode: string): string => {
        switch (statusCode) {
            case `${ParamEnum.PQR_STATUS}_ANU`:
                return `Nos permitimos informar que la PQR consultada se encuentra en estado anulada por encontrarse duplicada o repetida en el sistema.`;
            case `${ParamEnum.PQR_STATUS}_CER`: //Only applies if is not survey yet
                return `La PQR consultada ya tiene encuesta realizada.`;
            default:
                return 'En el momento el trámite de su PQR no ha sido finalizado.';
        }
    };

    useEffect(() => {
        if (survey !== undefined) {
            handleFormClear();
        }
    }, [survey]);

    return {
        formik,
        types,
        handleFormSubmit,
        results,
        hasSearched,
        handleFormClear,
        getSearchStatusMessage,
        getSurveyStatusMessage,
        isLoading,
        isCloseStatus,
        isResponseSurvey,
        isMessageInfo,
        isSolvedStatus,
    };
};
